/* eslint-disable @typescript-eslint/tslint/config */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/unbound-method */
import { Component, inject, OnInit } from '@angular/core';
import { maskValue } from '@workflow/directive/input-mask/mask.helper';
import { FieldSummary } from '@workflow/ecp-workflow/dynamic-app/types/review-data.type';
import { ReviewBaseComponent } from '../review-base.component';
import { FieldInstance, FieldService } from '@workflow/class/field.service';

@Component({
  selector: 'ecp-review-card',
  templateUrl: './review-card.component.html'
})
export class ReviewCardComponent extends ReviewBaseComponent implements OnInit {
  private readonly fieldService = inject(FieldService);
  public displayFields: FieldSummary[];

  /**
   * page iteration id for co-borrower, undefined for primary borrower
   */
  private pageIteration: number;

  /** Beneficiary Page ID */
  private readonly OAO_ADDITIONAL_CARD_ID = 'oao-additional-question';
  private readonly OLA_ADDITIONAL_CARD_ID = 'ola-additional-question';
  private readonly OAO_BENEFICIARY_PAGE_ID = 'oao-add-beneficiaries';
  private labelCount: string;
  public title: string;

  public ngOnInit(): void {
    this.pageIteration = this.card.pageIterationId;
    this.displayFields = this.getDisplayFields();
    this.title = this.getCardTitle();
  }

  /**
   * list of customized field processing based on specific card id
   */
  private readonly customProcessing: { [cardId: string]: () => FieldSummary[] } = {
    declarations: this.declarationsProcessing,
    'declarations-co-borrower-#': this.declarationsProcessing,
    'declarations-co-borrower-urla-#': this.declarationsProcessing,
    'declarations-urla': this.declarationsProcessing,
    ethnicity: this.demographicProcessing,
    'ethnicity-co-borrower-#': this.demographicProcessing,
    gender: this.demographicProcessing,
    'gender-co-borrower-#': this.demographicProcessing,
    'other-income': this.otherIncomeProcessing,
    'other-income-co-borrower-#': this.otherIncomeProcessing,
    race: this.demographicProcessing,
    'race-co-borrower-#': this.demographicProcessing,
    'ola-heloc-ethnicity': this.demographicProcessing,
    'ola-heloc-ethnicity-co-borrower-#': this.demographicProcessing,
    'ola-heloc-gender': this.demographicProcessing,
    'ola-heloc-gender-co-borrower-#': this.demographicProcessing,
    'ola-heloc-race': this.demographicProcessing,
    'ola-heloc-race-co-borrower-#': this.demographicProcessing
  };

  public getCardTitle(): string {
    const card = this.card;

    if (this.card.id === this.OAO_ADDITIONAL_CARD_ID || this.card.id === this.OLA_ADDITIONAL_CARD_ID) {
      if (this.checkForAdditionalQues()) {
        return card.config['summary-title'] || card.customTitleOrDefault;
      }
      return null;
    }

    if (Array.isArray(card.config.title) && !card.config.titleFn) {
      const index = card.iterationId;
      const replacementId = index >= 0 ? index + 1 : '';
      return (
        (
          (index !== undefined && card.config.title[index]?.replace(/ {{\$}}/g, ' ' + replacementId)) ||
          card.config.title[card.config.title.length - 1]
        ).replace(/ {{\$}}/g, ' ' + replacementId) ||
        (card.config.title && card.config.title[0].replace(/ {{\$}}/g, ' ' + replacementId))
      );
    }
    if (card.config.id === this.OAO_BENEFICIARY_PAGE_ID) {
      return 'Any Beneficiaries?';
    }
    return card.config['summary-title'] || card.customTitleOrDefault;
  }

  /**
   * complete list of fields and nested fields to be displayed
   */
  public getDisplayFields(): FieldSummary[] {
    if (this.customProcessing[this.card.config.id] && typeof this.customProcessing[this.card.config.id] === 'function') {
      return this.customProcessing[this.card.config.id].apply(this);
    }
    return this.genericProcessing();
  }

  public checkFieldGroupCard(item: FieldSummary): boolean {
    return item?.field?.config?.type === 'field-group-card';
  }

  public checkBeneficiaryAddress(item: FieldSummary): boolean {
    return item?.field?.config?.id === 'oao-beneficiary-addlabel-$';
  }

  private otherIncomeProcessing(): FieldSummary[] {
    const coApplicant = this.pageIteration >= 0;
    const fields = {
      additional: coApplicant ? 'additional-income-co-borrower-#' : 'additional-income',
      group: coApplicant ? 'potential-income-sources-group-co-borrower-#' : 'potential-income-sources-group'
    };
    if (this.fieldService.createField(fields.additional, null, this.pageIteration).data.value) {
      return (
        this.fieldService
          .createField(fields.group, null, this.pageIteration)
          // eslint-disable-next-line max-len
          .nestedFields.filter((field) => field.id.indexOf('-cb') > -1 && (field.data.value as boolean)) // show amount only when cb is checked/ true
          .map((cbField) => {
            const amount = this.fieldService.createField(cbField.id.replace(/-cb$/, '-amount'), cbField.flowId);
            return <FieldSummary>{
              field: amount,
              fieldData: { data: amount.data.value, label: cbField.config.label }
            };
          })
      );
    }
    return [{ fieldData: { label: this.fieldService.createField(fields.additional, null, this.pageIteration).config.label, data: 'No' } }];
  }

  private demographicProcessing(): FieldSummary[] {
    const list = this.card.fields.filter((f) => f.visible && f.config.type === 'field-group');
    const result: FieldSummary[] = [];

    for (const field of list) {
      if (this.fieldService.createField(field.config.id.replace('-group', '-consent'), null, null, this.pageIteration).data.value) {
        result.push({ fieldData: { label: field.config.label, data: 'Decline to provide', layout: 'noLabel' } });
      } else {
        const data = this.consentCheck(field);
        result.push({ fieldData: { label: field.config.label, data, layout: 'noLabel' } });
      }
    }
    return result;
  }

  private consentCheck(field: FieldInstance): string {
    let data = field.nestedFields
      .filter((nested) => nested.config.id.indexOf('-consent') === -1 && nested.data.value)
      .map((f, i) => {
        let separator = '';
        if (i > 0) {
          separator = ', ';
        }
        return f.config?.label && f.config.type === 'checkbox' ? `${separator}${f.config.label}` : `: ${f.readableValue}`;
      })
      .reduce((acc, current) => `${acc}${current}`, '');
    if (data === 'Yes') {
      data = '';
    }
    return data;
  }

  private declarationsProcessing(): FieldSummary[] {
    const list = this.genericProcessing();
    let count = 0;
    return list.map(
      (summary) =>
        <FieldSummary>{
          field: summary.field,
          fieldData: {
            data: summary.field.readableValue,
            labelCount:
              summary.field.id === 'declaration-new-credit-urla' ||
              summary.field.id === `declaration-new-credit-co-borrower-urla-${this.card.pageIterationId}`
                ? ''
                : this.reviewPageAlphaOrder(count++) + '.',
            label: summary.field.config.label,
            layout: 'long-questions'
          }
        }
    );
  }
  private reviewPageAlphaOrder(value): string {
    this.labelCount = (value + 10).toString(36).toUpperCase();
    return this.labelCount;
  }

  /**
   * complete list of fields and nested fields to be displayed
   */
  private genericProcessing(): FieldSummary[] {
    const cardFields = this.card.fields.filter(
      (field) => this.fieldFilter(field) && !(field.config.hideIfEmpty && field.data.valueStringOnly === '')
    );
    let list: FieldSummary[] = [];

    for (const field of cardFields) {
      if (field.config.type === 'field-group' || field.config.type === 'field-group-card') {
        if (field.config.type === 'field-group-card') {
          list = list.concat([{ field }]);
        }
        if (field.config.id === 'loan-term-amortization-row-value-group') {
          list = list.concat(<FieldSummary>{
            fieldData: {
              label: field.label,
              data: field.nestedFields
                .map((nested) =>
                  nested.data.value
                    ? `${nested.data.valueString} ${nested.config.postfix ? this.parseText(nested.config.postfix) : ''}`
                    : ''
                )
                .reduce((total, current) => `${total} ${current}`, '')
            }
          });
        } else if (
          field.config.id === 'oao-bsa-productsandservicesofferings-list' ||
          field.config.id === 'oao-bsa-fields-achdeposits-list' ||
          field.config.id === 'oao-bsa-fields-achwithdrawls-list'
        ) {
          list = list.concat(<FieldSummary>{
            fieldData: {
              label: field.label,
              data: field.nestedFields
                .filter((t) => t.data.value)
                .map((nested) => (nested.data.value ? `${nested.config.label}` : ''))
                .join(', ')
            }
          });
        } else if (field.config.id === 'oao-bsa-fields-publicofficeforgeingovernments-list') {
          list = list.concat(<FieldSummary>{
            fieldData: {
              label: field.label,
              data: '<p>' + this.getBSAPOFGHasValue().join('</p><p>') + '</p>'
            }
          });
        } else if (field.config.id === 'ola-heloc-property-tax' || field.config.id === 'ola-heloc-homeowner-insurance') {
          // eslint-disable-next-line eqeqeq
          const frequency = field.nestedFields[1].data.value == 'Annual' ? 'year' : 'month';
          list = list.concat(<FieldSummary>{
            fieldData: {
              label: field.nestedFields[0].label,
              data: field.nestedFields[0].data.value
                ? `<span>${maskValue(field.nestedFields[0].config.mask, field.nestedFields[0].data.valueStringOnly)}
                  /${frequency}<br/>${field.nestedFields[2].data.value ? 'included in payment' : ''}</span>`
                : ''
            }
          });
        } else {
          let nestedFields = this.getNestedFields(field);
          nestedFields.forEach((element) => {
            if (element.config.type === 'field-group' || field.config.type === 'field-group-card') {
              nestedFields = nestedFields.concat(this.getNestedFields(element));
            }
          });
          list = list.concat(nestedFields.map((f) => <FieldSummary>{ field: f }));
        }
      } else {
        if (field.config.type === 'info-row' && field.config.id === 'loan-loan-terms-row') {
          list.push(<FieldSummary>{
            fieldData: {
              label: field.label,
              data: field.data.value ? `${field.data.valueString} ${field.config.postfix ? this.parseText(field.config.postfix) : ''}` : ''
            }
          });
        } else {
          list.push(<FieldSummary>{ field });
        }
      }
    }

    if (list && list.length > 0) {
      // eslint-disable-next-line eqeqeq
      list = list.filter((t) => !t.field || !t.field.config || !t.field.config.type || t.field.config.type != 'field-group');
    }
    return list;
  }

  private getNestedFields(field: FieldInstance): FieldInstance[] {
    return field.nestedFields.filter((nested) => this.fieldFilter(nested));
  }

  private fieldFilter(field: FieldInstance): boolean {
    return !field.hideInReview && field.config.type !== 'button' && (field.config.alwaysShowInReview || (field.visible && field.enabled));
  }

  private checkForAdditionalQues() {
    let pathPrefix = 'api:oaoMemberInformation/';
    if (this.card.id === this.OLA_ADDITIONAL_CARD_ID) {
      pathPrefix = 'ola:olaApplicationInformation/';
    }
    const customFieldPath = `${pathPrefix}customField/applicationCustomFields`;
    const labelpath = 'customField/applicationCustomFields/$/fieldLabel';
    const customFieldSize = this.statefulService.arraySize(customFieldPath);
    if (customFieldSize > 0) {
      for (let index = 0; index < customFieldSize; index++) {
        const fieldLabelValue = this.statefulService.wrap(`${pathPrefix}${labelpath}`, index).valueString;
        // Since we have initialized 20 items into the bundle during creation and
        // there's a possibility of race condition with bundle data not yet being updated with the API response
        // Added a null check for field label or question value so that Card title shows up only if field labels have actual values
        if (fieldLabelValue) {
          return true;
        }
      }
    }
    return false;
  }

  public disableCardEdit(): boolean {
    let isAnyAccepted = false;
    if (this.flow.id === 'ola' || this.flow.id.indexOf('ola-consumer') > -1) {
      const storagePath = 'ola:olaApplicationInformation/disclosures';
      const disclosuresCount = this.statefulService.arraySize(storagePath);
      for (let i = 0; i < disclosuresCount; i++) {
        const applicantsCount = this.statefulService.arraySize(storagePath + `/${i}/applicants`);
        for (let j = 0; j < applicantsCount; j++) {
          const isAccept = this.statefulService.wrap(storagePath + `/${i}/applicants/${j}/isAccept`).value as boolean;
          if (isAccept) {
            isAnyAccepted = true;
          }
        }
      }
    }

    return isAnyAccepted;
  }

  public hideEligibilityQuestions(cardId: string): boolean {
    const allowEligiblityQuestions = this.stringVariableService.parse('config:admin/canShowEligibilityQuestions') as boolean;
    const alreadyMember = this.statefulService.wrap('api:oaoMemberInformation/alreadyMember').value as boolean;
    return !(cardId === 'oao-eligibility-selection' && alreadyMember && !allowEligiblityQuestions);
  }

  /** Method to get any of bsa (Public Office in Forgein Governments) checkbox checked */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getBSAPOFGHasValue = (): any => {
    const storagePathPrefix = 'api:oaoMemberInformation/businessInformation/bankSecrecyAct/publicOfficeForgeinGovernmentsList';
    const lstArray = [];

    const ResponsiblePartyCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/responsiblePartyCount'
    ).valueStringOnly;
    if (ResponsiblePartyCount && Number.parseInt(ResponsiblePartyCount) > 0) {
      for (let i = 0; i < Number.parseInt(ResponsiblePartyCount); i++) {
        const applicantName = this.statefulService.wrap(`${storagePathPrefix}/responsibleParties/${i}/applicantName`).valueStringOnly;
        const positionHeld = this.statefulService.wrap(`${storagePathPrefix}/responsibleParties/${i}/positionHeld`).valueStringOnly;
        const isSelected = this.statefulService.wrap(`${storagePathPrefix}/responsibleParties/${i}/isSelected`).value as boolean;
        if (isSelected) {
          lstArray.push(applicantName + ', ' + positionHeld);
        }
      }
    }

    const BeneficialOwnerCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalBeneficialOwnerCount'
    ).valueStringOnly;
    if (BeneficialOwnerCount && Number.parseInt(BeneficialOwnerCount) > 0) {
      for (let i = 0; i < Number.parseInt(BeneficialOwnerCount); i++) {
        const applicantName = this.statefulService.wrap(`${storagePathPrefix}/beneficialOwner/${i}/applicantName`).valueStringOnly;
        const positionHeld = this.statefulService.wrap(`${storagePathPrefix}/beneficialOwner/${i}/positionHeld`).valueStringOnly;
        const isSelected = this.statefulService.wrap(`${storagePathPrefix}/beneficialOwner/${i}/isSelected`).value as boolean;
        if (isSelected) {
          lstArray.push(applicantName + ', ' + positionHeld);
        }
      }
    }

    const additionalAuthorizedCount = this.statefulService.wrap(
      'api:oaoMemberInformation/businessInformation/additionalAuthorizedCount'
    ).valueStringOnly;
    if (additionalAuthorizedCount && Number.parseInt(additionalAuthorizedCount) > 0) {
      for (let i = 0; i < Number.parseInt(additionalAuthorizedCount); i++) {
        const applicantName = this.statefulService.wrap(`${storagePathPrefix}/authorizedSigners/${i}/applicantName`).valueStringOnly;
        const positionHeld = this.statefulService.wrap(`${storagePathPrefix}/authorizedSigners/${i}/positionHeld`).valueStringOnly;
        const isSelected = this.statefulService.wrap(`${storagePathPrefix}/authorizedSigners/${i}/isSelected`).value as boolean;
        if (isSelected) {
          lstArray.push(applicantName + ', ' + positionHeld);
        }
      }
    }

    return lstArray;
  };

  // Used to hide Edit button if card is membership or loan selection
  // We do not want the user to change their product selection after they have already selected one
  public get isNotProductSelection(): boolean {
    const productSelectionCards = ['oao-membership-selection', 'oao-membership-selection-grid', 'ola-consumer-loan-types'];
    return productSelectionCards.every((cardId) => {
      return cardId !== this.card?.config?.id;
    });
  }
}
